import { create } from "zustand";
import { TGuestStore } from "./Store.types";
import { createJSONStorage, persist } from "zustand/middleware";
import {
  TPhotoChallengeDone,
  TTeamMatchResult,
  TBingoChallengeDone,
  TBingoAchievements,
} from "services/types";

const defaultGames = {
  status: {
    photo_challenge_intro_seen: false,
    photo_challenge_congrats_seen: false,
    quiz_intro_seen: false,
    quiz_congrats_seen: false,
    team_match_intro_seen: false,
    team_match_congrats_seen: false,
    bingo_intro_seen: false,
    bingo_row_congrats_seen: false,
    bingo_column_congrats_seen: false,
    bingo_fullcard_congrats_seen: false,
  },
  quizResult: null,
  photoChallengesDone: [],
  teamMatchResult: null,
  bingoChallengesDone: [],
  bingoAchievements: {
    firstRow: null,
    firstColumn: null,
    fullCard: null,
  },
};

const defaultUploadStats = {
  totalFiles: 0,
  totalMegabytes: 0,
};

export const useGuestStore = create<TGuestStore>()(
  persist(
    (set, get) => ({
      guest: null,
      games: defaultGames,
      uploadStats: defaultUploadStats,
      setGuest: (guest) => set({ guest }),
      setQuizResult: (quizResult) =>
        set((state) => ({
          games: {
            ...state.games,
            quizResult,
          },
        })),
      addPhotoChallengeDone: (result: TPhotoChallengeDone) =>
        set((state) => ({
          games: {
            ...state.games,
            photoChallengesDone: [...state.games.photoChallengesDone, result],
          },
        })),
      updatePhotoChallengeDone: (result: TPhotoChallengeDone) =>
        set((state) => {
          const index = state.games.photoChallengesDone.findIndex(
            (item) => item.challengeId === result.challengeId
          );

          if (index === -1) return state;

          const newPhotoChallengeDones = [...state.games.photoChallengesDone];
          newPhotoChallengeDones[index] = result;

          return {
            games: {
              ...state.games,
              photoChallengesDone: newPhotoChallengeDones,
            },
          };
        }),
      removePhotoChallengeDone: (challengeId: string) =>
        set((state) => ({
          games: {
            ...state.games,
            photoChallengesDone: state.games.photoChallengesDone.filter(
              (item) => item.challengeId !== challengeId
            ),
          },
        })),
      addBingoChallengeDone: (result: TBingoChallengeDone) =>
        set((state) => ({
          games: {
            ...state.games,
            bingoChallengesDone: [...state.games.bingoChallengesDone, result],
          },
        })),
      updateBingoChallengeDone: (result: TBingoChallengeDone) =>
        set((state) => {
          const index = state.games.bingoChallengesDone.findIndex(
            (item) => item.challengeId === result.challengeId
          );

          if (index === -1) return state;

          const newBingoChallengesDone = [...state.games.bingoChallengesDone];
          newBingoChallengesDone[index] = result;

          return {
            games: {
              ...state.games,
              bingoChallengesDone: newBingoChallengesDone,
            },
          };
        }),
      removeBingoChallengeDone: (challengeId: number) =>
        set((state) => ({
          games: {
            ...state.games,
            bingoChallengesDone: state.games.bingoChallengesDone.filter(
              (item) => item.challengeId !== challengeId
            ),
          },
        })),
      setBingoAchievements: (achievements: TBingoAchievements) =>
        set((state) => ({
          games: {
            ...state.games,
            bingoAchievements: achievements,
          },
        })),
      setTeamMatchResult: (teamMatchResult: TTeamMatchResult) =>
        set((state) => ({
          games: {
            ...state.games,
            teamMatchResult,
          },
        })),
      setGameStatus: (key, value) =>
        set((state) => ({
          games: {
            ...state.games,
            status: {
              ...state.games.status,
              [key]: value,
            },
          },
        })),
      getGameStatus: (key) => get().games.status[key],
      incrementUploadStats: (fileSizeBytes: number) =>
        set((state) => ({
          uploadStats: {
            totalFiles: state.uploadStats.totalFiles + 1,
            totalMegabytes: state.uploadStats.totalMegabytes + (fileSizeBytes / (1024 * 1024)),
          },
        })),
      clearStore: () => {
        set({
          guest: null,
          games: defaultGames,
          uploadStats: defaultUploadStats,
        });
      },
    }),
    {
      name: "guest-storage",
      storage: createJSONStorage(() => localStorage),
    }
  )
);
